import { useForm, usePage, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Pagination } from "@/components/ui/pagination"
import { CompanyForm, emptyCompanyFormData, type CompanyFormData } from "@/components/CompanyForm"
import { CompanyTable } from "@/components/CompanyTable"
import { FlashMessages } from "@/components/FlashMessages"
import { SearchInput } from "@/components/SearchInput"
import { Plus, Building2 } from "lucide-react"
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback"
import { formatCNPJ, formatPhone, stripMask } from "@/lib/masks"
import type { Company, CustomersPageProps } from "@/types/company"

export default function Customers() {
  const { t } = useTranslation()
  const { auth, flash, customers, pagination, filters } = usePage<CustomersPageProps>().props
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Company | null>(null)
  const [searchValue, setSearchValue] = useState(filters.search)

  const createForm = useForm<CompanyFormData>({ ...emptyCompanyFormData })
  const editForm = useForm<CompanyFormData>({ ...emptyCompanyFormData })

  const debouncedSearch = useDebouncedCallback((value: string) => {
    router.get("/customers", {
      search: value || undefined,
      sort: filters.sort,
      direction: filters.direction,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }, 300)

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(e.target.value)
    debouncedSearch(e.target.value)
  }

  function handleSort(column: string) {
    const newDirection = filters.sort === column && filters.direction === "asc" ? "desc" : "asc"
    router.get("/customers", {
      search: filters.search || undefined,
      sort: column,
      direction: newDirection,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }

  function goToPage(page: number) {
    router.get("/customers", {
      search: filters.search || undefined,
      sort: filters.sort,
      direction: filters.direction,
      page,
    }, { preserveState: true, preserveScroll: true })
  }

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    // Strip masks before submitting
    createForm.setData({
      ...createForm.data,
      tax_id: stripMask(createForm.data.tax_id),
      phone: stripMask(createForm.data.phone),
    })
    createForm.post("/customers", {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingCustomer) return

    // Strip masks before submitting
    editForm.setData({
      ...editForm.data,
      tax_id: stripMask(editForm.data.tax_id),
      phone: stripMask(editForm.data.phone),
    })
    editForm.patch(`/customers/${editingCustomer.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingCustomer(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(customer: Company) {
    setEditingCustomer(customer)
    editForm.setData({
      name: customer.name,
      tax_name: customer.tax_name || "",
      tax_id: formatCNPJ(customer.tax_id || ""),
      email: customer.email || "",
      phone: formatPhone(customer.phone || ""),
      street: customer.street || "",
      city: customer.city || "",
      state: customer.state || "",
    })
  }

  function handleDelete(customer: Company) {
    if (confirm(t("customers.confirmDelete"))) {
      router.delete(`/customers/${customer.id}`)
    }
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <PageHeader
          eyebrow={t("customers.eyebrow", { defaultValue: "Parceiros comerciais" })}
          title={t("customers.title")}
          subtitle={t("customers.subtitle")}
          actions={
            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
              setIsCreateDialogOpen(open)
              if (!open) createForm.reset()
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("customers.addButton")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleCreateSubmit}>
                  <DialogHeader>
                    <DialogTitle>{t("customers.addButton")}</DialogTitle>
                    <DialogDescription>{t("customers.addDescription")}</DialogDescription>
                  </DialogHeader>
                  <CompanyForm
                    data={createForm.data}
                    errors={createForm.errors}
                    onChange={(field, value) => createForm.setData(field, value)}
                    idPrefix="create"
                    translationPrefix="customers"
                  />
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      {t("common.cancel")}
                    </Button>
                    <Button type="submit" disabled={createForm.processing}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t("common.add")}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          }
        />

          <Dialog open={!!editingCustomer} onOpenChange={(open) => !open && setEditingCustomer(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("customers.editCustomer")}</DialogTitle>
                  <DialogDescription>{t("customers.editDescription")}</DialogDescription>
                </DialogHeader>
                <CompanyForm
                  data={editForm.data}
                  errors={editForm.errors}
                  onChange={(field, value) => editForm.setData(field, value)}
                  idPrefix="edit"
                  translationPrefix="customers"
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditingCustomer(null)}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display tracking-tight">
              <Building2 className="w-5 h-5" />
              {t("customers.section")}
            </CardTitle>
            <CardDescription>{t("customers.sectionDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(customers.length > 0 || filters.search) && (
              <SearchInput
                value={searchValue}
                onChange={handleSearchChange}
                placeholder={t("customers.searchPlaceholder")}
              />
            )}

            {customers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {filters.search ? t("customers.noSearchResults") : t("customers.noCustomers")}
              </p>
            ) : (
              <>
                <CompanyTable
                  data={customers}
                  filters={filters}
                  onSort={handleSort}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                  translationPrefix="customers"
                />
                <Pagination pagination={pagination} onPageChange={goToPage} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
